import { Component } from '@theme/component';

/**
 * Menu Navigation Component
 * Manages styling and layout for menu navigation
 * Uses slideshow-component for dragging functionality
 */
export class MenuNavigationComponent extends Component {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.slideshow = null;
    /** @type {(() => void) | null} */
    this.handleResize = null;
    /** @type {ResizeObserver | null} */
    this.resizeObserver = null;
  /** @type {ResizeObserver | null} */
  this.widthResizeObserver = null;
  /** @type {ResizeObserver | null} */
  this.contentResizeObserver = null;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Update sticky position, width, and border-radius classes on section wrapper
    this.updateStickyPosition();
    this.updateSectionWidth();
    this.updateBorderRadius();
    
    // Listen for window resize to update responsive classes
    this.handleResize = () => {
      this.updateStickyPosition();
      this.updateSectionWidth();
      this.updateBorderRadius();
      this.updateContentAlignment();
    };
    window.addEventListener('resize', this.handleResize);
    
    // Find the slideshow component
    this.slideshow = this.querySelector('slideshow-component');
    
    // Scroll active link into view
    this.scrollActiveLinkIntoView();
    
    // Update content alignment based on overflow
    this.updateContentAlignment();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
      this.handleResize = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.widthResizeObserver) {
      this.widthResizeObserver.disconnect();
      this.widthResizeObserver = null;
    }
    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
  }

  /**
   * Scroll the active link into view within the navigation container
   */
  scrollActiveLinkIntoView() {
    if (!this.slideshow) return;
    
    const activeLink = this.querySelector('.menu-navigation__link--current');
    if (!activeLink) return;
    
    const scroller = this.slideshow.querySelector('slideshow-slides');
    if (!scroller) return;

    // Wait for layout to complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const linkRect = activeLink.getBoundingClientRect();
        const containerRect = scroller.getBoundingClientRect();

        const linkLeft = linkRect.left - containerRect.left + scroller.scrollLeft;
        const linkRight = linkLeft + linkRect.width;
        const containerWidth = containerRect.width;
        const scrollLeft = scroller.scrollLeft;

        // If link is outside viewport, scroll it into view
        if (linkLeft < scrollLeft) {
          scroller.scrollTo({
            left: linkLeft - 20, // Add some padding
            behavior: 'smooth'
          });
        } else if (linkRight > scrollLeft + containerWidth) {
          scroller.scrollTo({
            left: linkRight - containerWidth + 20, // Add some padding
            behavior: 'smooth'
          });
        }
      });
    });
  }

  /**
   * Update section width class on section wrapper
   */
  updateSectionWidth() {
    const sectionWidthMobile = this.dataset.sectionWidthMobile || 'full-width';
    const sectionWidthDesktop = this.dataset.sectionWidthDesktop || 'full-width';
    const sectionWrapper = this.closest('.shopify-section.menu-section');
    const isDesktop = window.innerWidth >= 750;
    
    if (sectionWrapper instanceof HTMLElement) {
      // Remove existing width classes from section wrapper
      sectionWrapper.classList.remove(
        'menu-section--fit-width-mobile',
        'menu-section--full-width-mobile',
        'menu-section--fit-width-desktop',
        'menu-section--full-width-desktop'
      );
      
      const currentWidth = isDesktop ? sectionWidthDesktop : sectionWidthMobile;
      
      if (currentWidth === 'fit-width') {
        // Add fit-width class to wrapper (mobile or desktop)
        const widthClass = isDesktop ? 'menu-section--fit-width-desktop' : 'menu-section--fit-width-mobile';
        sectionWrapper.classList.add(widthClass);
        
        // Find slideshow-slides element to measure its width
        const slideshowSlides = this.querySelector('slideshow-slides');
        
        if (slideshowSlides) {
          // Calculate and set width as CSS variable based on slideshow-slides
          const updateWidth = () => {
            // Use scrollWidth to get the full content width, or clientWidth if scrollWidth is smaller
            const width = Math.max(slideshowSlides.scrollWidth, slideshowSlides.clientWidth);
            // Apply width variable to the section wrapper (menu-section)
            const widthVar = isDesktop ? '--menu-section-width-desktop' : '--menu-section-width-mobile';
            sectionWrapper.style.setProperty(widthVar, `${width}px`);
          };
          
          // Set initial width after a small delay to ensure content is rendered
          requestAnimationFrame(() => {
            requestAnimationFrame(updateWidth);
          });
          
          // Update on resize
          if (!this.widthResizeObserver) {
            this.widthResizeObserver = new ResizeObserver(updateWidth);
            this.widthResizeObserver.observe(slideshowSlides);
          }
        }
      } else {
        // Remove width variable for full-width
        const widthVar = isDesktop ? '--menu-section-width-desktop' : '--menu-section-width-mobile';
        sectionWrapper.style.removeProperty(widthVar);
        const widthClass = isDesktop ? 'menu-section--full-width-desktop' : 'menu-section--full-width-mobile';
        sectionWrapper.classList.add(widthClass);
        
        // Only disconnect observer if both mobile and desktop are full-width
        if (sectionWidthMobile === 'full-width' && sectionWidthDesktop === 'full-width') {
          if (this.widthResizeObserver) {
            this.widthResizeObserver.disconnect();
            this.widthResizeObserver = null;
          }
        }
      }
    }
  }

  /**
   * Update border-radius CSS variables on section wrapper
   */
  updateBorderRadius() {
    const borderRadiusMobile = this.dataset.borderRadiusMobile || '0';
    const borderRadiusDesktop = this.dataset.borderRadiusDesktop || '0';
    const sectionWrapper = this.closest('.shopify-section.menu-section');
    
    if (sectionWrapper instanceof HTMLElement) {
      // Set border-radius CSS variables for mobile and desktop
      sectionWrapper.style.setProperty('--menu-section-border-radius-mobile', `${borderRadiusMobile}px`);
      sectionWrapper.style.setProperty('--menu-section-border-radius-desktop', `${borderRadiusDesktop}px`);
    }
  }

  /**
   * Update content alignment based on overflow
   * Center if content fits, align left if it overflows
   */
  updateContentAlignment() {
    const slideshowSlides = this.querySelector('.menu-navigation__slideshow slideshow-slides');
    if (!slideshowSlides) return;
    
    const checkAlignment = () => {
      // Check if content overflows
      const overflows = slideshowSlides.scrollWidth > slideshowSlides.clientWidth;
      
      if (overflows) {
        // Content overflows: align left
        slideshowSlides.classList.remove('menu-navigation__slides--centered');
      } else {
        // Content fits: center
        slideshowSlides.classList.add('menu-navigation__slides--centered');
      }
    };
    
    // Initial check after layout
    requestAnimationFrame(() => {
      requestAnimationFrame(checkAlignment);
    });
    
    // Watch for size changes
    if (!this.contentResizeObserver) {
      this.contentResizeObserver = new ResizeObserver(() => {
        checkAlignment();
      });
      this.contentResizeObserver.observe(slideshowSlides);
    } else {
      // If observer already exists, ensure we're observing the current element
      // (in case DOM was reconstructed)
      try {
        this.contentResizeObserver.observe(slideshowSlides);
      } catch (e) {
        // Element already observed, which is fine
      }
    }
  }

  /**
   * Update sticky position class on section wrapper
   */
  updateStickyPosition() {
    const stickyEnabledMobile = this.dataset.stickyEnabledMobile === 'true';
    const stickyEnabledDesktop = this.dataset.stickyEnabledDesktop === 'true';
    const stickyPositionMobile = this.dataset.stickyPositionMobile || 'top';
    const stickyPositionDesktop = this.dataset.stickyPositionDesktop || 'top';
    const sectionWrapper = this.closest('.shopify-section.menu-section');
    const isDesktop = window.innerWidth >= 750;
    
    if (sectionWrapper instanceof HTMLElement) {
      // Remove existing sticky position classes
      sectionWrapper.classList.remove(
        'menu-section--sticky-enabled-mobile',
        'menu-section--sticky-enabled-desktop',
        'menu-section--sticky-top-mobile',
        'menu-section--sticky-bottom-mobile',
        'menu-section--sticky-top-desktop',
        'menu-section--sticky-bottom-desktop'
      );
      
      // Add sticky enabled classes if sticky is enabled
      if (stickyEnabledMobile) {
        sectionWrapper.classList.add('menu-section--sticky-enabled-mobile');
        sectionWrapper.classList.add(`menu-section--sticky-${stickyPositionMobile}-mobile`);
      }
      
      if (stickyEnabledDesktop) {
        sectionWrapper.classList.add('menu-section--sticky-enabled-desktop');
        sectionWrapper.classList.add(`menu-section--sticky-${stickyPositionDesktop}-desktop`);
      }
      
      // If sticky bottom (mobile or desktop), set CSS variable with section height
      const needsHeight = (stickyEnabledMobile && stickyPositionMobile === 'bottom') || 
                          (stickyEnabledDesktop && stickyPositionDesktop === 'bottom');
      
      if (needsHeight) {
        const updateHeight = () => {
          const height = sectionWrapper.getBoundingClientRect().height;
          sectionWrapper.style.setProperty('--menu-section-height', `${height}px`);
        };
        
        // Set initial height
        updateHeight();
        
        // Update on resize
        if (!this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(updateHeight);
          this.resizeObserver.observe(sectionWrapper);
        }
      } else {
        // Remove height variable if sticky bottom is not needed
        sectionWrapper.style.removeProperty('--menu-section-height');
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }
    }
  }
}

if (!customElements.get('menu-navigation')) {
  customElements.define('menu-navigation', MenuNavigationComponent);
}

// Listen for theme editor updates
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const customEvent = /** @type {CustomEvent<{sectionId: string}>} */ (event);
    const menuNav = document.querySelector(`menu-navigation[data-section-id="${customEvent.detail.sectionId}"]`);
    if (menuNav && menuNav instanceof MenuNavigationComponent) {
      menuNav.updateStickyPosition();
      menuNav.updateSectionWidth();
      menuNav.updateBorderRadius();
      menuNav.updateContentAlignment();
    }
  });
}

